import { formatDate, localizeNumber } from "../../Helper/generalhelper";
import { padToThreeDigits } from "../../Helper/PadToThreeDigits";
import { clientStatusDropdown } from "../../Helper/Constants";
import { valuesAreEquivalent } from "./scoopChangedFields";

const CLIENT_INVOICE_STATUS = "invoice-client";

// Labels from ViewInvoice.js / clientStatusDropdown. Unknown keys stay hidden.
const CLIENT_FIELD_LABELS = {
  invoice_date: "Date",
  item_total: "Subtotal excl. TAX",
  vat: "% TAX",
  vat_amount: "% TAX",
  Invoice_cost: "Total Price",
  invoice_cost: "Total Price",
  scoop_additional_detail: "Description",
  scoop_additional_price: "Value",
  inv_notes: "Notes",
  invoice_due_date: "Due Date",
  invoice_number: "Invoice #",
  custom_invoice_number: "Invoice #",
  invoice_status: "Invoice Status",
  paid_date: "Payment Date",
  paid_amount: "Paid Amount",
  scoop_id: "Project",
  deleted_scoopId: "Project",
};

const DATE_KEYS = new Set(["invoice_date", "invoice_due_date", "paid_date"]);
const MONEY_KEYS = new Set([
  "item_total",
  "vat_amount",
  "Invoice_cost",
  "invoice_cost",
  "scoop_additional_price",
  "paid_amount",
]);

const PREVIEW_LIMIT = 4;

const CLIENT_STATUS_LABELS = clientStatusDropdown.reduce((acc, item) => {
  if (item?.value != null) acc[item.value] = item.label;
  return acc;
}, {});

export function isClientInvoiceLog(logStatus) {
  return String(logStatus || "").trim().toLowerCase() === CLIENT_INVOICE_STATUS;
}

function parseDetails(details) {
  if (!details) return null;
  if (typeof details === "object") return details;
  try {
    return JSON.parse(details);
  } catch (e) {
    return null;
  }
}

function parseChangedFields(details) {
  const parsed = parseDetails(details);
  if (!parsed) return null;

  let changedFields = parsed.changedFields;
  if (typeof changedFields === "string") {
    try {
      changedFields = JSON.parse(changedFields);
    } catch (e) {
      return null;
    }
  }

  if (!changedFields || typeof changedFields !== "object" || Array.isArray(changedFields)) {
    return null;
  }

  return changedFields;
}

function looksLikeJson(value) {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return trimmed.startsWith("{") || trimmed.startsWith("[");
}

function parseMaybeJson(value) {
  if (!looksLikeJson(value)) return value;
  try {
    return JSON.parse(value.trim());
  } catch (e) {
    return value;
  }
}

function isEmptyValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" || trimmed.toLowerCase() === "null" || trimmed === "[]" || trimmed === "{}";
  }
  return false;
}

function isZeroDate(value) {
  if (value === null || value === undefined) return false;
  return String(value).trim().startsWith("0000-00-00");
}

function valueForCompare(fieldKey, value) {
  if (DATE_KEYS.has(fieldKey) && isZeroDate(value)) return "";
  return value;
}

function isZeroOrEmptyNumber(value) {
  if (isEmptyValue(value)) return true;
  if (typeof value === "boolean") return false;
  const raw = String(value).trim().replace(/\s/g, "").replace(",", ".");
  if (!/^-?\d+(\.\d+)?$/.test(raw)) return false;
  return Number(raw) === 0;
}

function invoiceValuesAreEquivalent(fieldKey, oldValue, newValue) {
  if (DATE_KEYS.has(fieldKey)) {
    return formatDateValue(oldValue) === formatDateValue(newValue);
  }
  if ((fieldKey === "vat" || MONEY_KEYS.has(fieldKey)) && isZeroOrEmptyNumber(oldValue) && isZeroOrEmptyNumber(newValue)) {
    return true;
  }
  return valuesAreEquivalent(valueForCompare(fieldKey, oldValue), valueForCompare(fieldKey, newValue), fieldKey);
}

function formatMoney(value) {
  if (isEmptyValue(value)) return "";
  return String(localizeNumber(value));
}

function formatDateValue(value) {
  if (isEmptyValue(value) || isZeroDate(value)) return "";
  return formatDate(String(value).trim(), { includeTime: false });
}

function formatScoopNumber(value) {
  if (isEmptyValue(value)) return "";
  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) return String(value).trim();
  return padToThreeDigits(parsed);
}

function formatScoopIdList(value) {
  const parsed = parseMaybeJson(value);
  if (isEmptyValue(parsed) || (Array.isArray(parsed) && !parsed.length)) return "";
  const rows = Array.isArray(parsed) ? parsed : [parsed];
  return rows
    .map(row => {
      const id = row && typeof row === "object" ? row.id : row;
      return formatScoopNumber(id);
    })
    .filter(Boolean)
    .join("\n");
}

function formatClientStatus(value) {
  if (isEmptyValue(value)) return "";
  const raw = String(value).trim();
  return CLIENT_STATUS_LABELS[raw] || raw;
}

function formatVatPercent(value) {
  if (isEmptyValue(value)) return "";
  return `${localizeNumber(value)} %`;
}

export function formatClientInvoiceFieldValue(fieldKey, value) {
  if (fieldKey === "vat") return formatVatPercent(value);
  if (fieldKey === "invoice_status") return formatClientStatus(value);
  if (fieldKey === "scoop_id") return formatScoopIdList(value);
  if (fieldKey === "deleted_scoopId") return formatScoopNumber(value);
  if (MONEY_KEYS.has(fieldKey)) return formatMoney(value);
  if (DATE_KEYS.has(fieldKey)) return formatDateValue(value);
  if (isEmptyValue(value)) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function getClientInvoiceChangedFields(details) {
  const changedFields = parseChangedFields(details);
  if (!changedFields) return [];

  return Object.keys(changedFields)
    .filter(key => CLIENT_FIELD_LABELS[key])
    .filter(key => {
      const change = changedFields[key];
      if (!change || typeof change !== "object" || Array.isArray(change)) return false;
      if (!Object.prototype.hasOwnProperty.call(change, "old") && !Object.prototype.hasOwnProperty.call(change, "new")) {
        return false;
      }
      return !invoiceValuesAreEquivalent(key, change.old, change.new);
    })
    .map(key => ({
      key,
      label: CLIENT_FIELD_LABELS[key],
      old: formatClientInvoiceFieldValue(key, changedFields[key].old),
      new: formatClientInvoiceFieldValue(key, changedFields[key].new),
    }))
    .filter(change => change.old !== change.new);
}

export function getClientInvoiceChangedSummary(changes) {
  const displayable = Array.isArray(changes) ? changes : [];
  const previewLabels = displayable.slice(0, PREVIEW_LIMIT).map(change => change.label);
  const remaining = Math.max(displayable.length - PREVIEW_LIMIT, 0);

  return {
    text: previewLabels.length ? `Changed: ${previewLabels.join(", ")}` : "",
    remaining,
  };
}
